import { PlancardsProps } from '@/components/PlanCards';
import { commonAction } from '../types/commonActions';
import { actionTypes } from './actionTypes';
import { createContext } from './createContext';
import { BenefitsType } from '@/types/tier';
import { useContext } from 'react';

export type productDataType = {
  status: string;
  selectedProduct: any;
  list: any[];
  selectedTier: any;
  selectedMultiYearPlan: any;
  benefits: BenefitsType[];
  errorMessage: string | null;
  amcPlans: PlancardsProps;
  perYearPrice: number;
  amcServiceDetails: any;
  disablePageScroll?: boolean;
  isLoading: boolean;
};

export type productDispatchType = {
  setProductsData: Function;
  setSelectedTier: Function;
  setSelectedMultiYearPlan: Function;
  setErrorMsg: Function;
  setAMCPlans: Function;
  setBenefits: Function;
  setPerYearPrice: Function;
  setAmcServiceDetails: Function;
  setDisablePageScroll: Function;
  setIsLoading: Function;
  setProductsList: Function;
};

export function productReducer(state: productDataType, action: commonAction) {
  const {
    selectedProduct = '',
    selectedTier = '',
    selectedMultiYearPlan = '',
    benefits = [],
    error = {},
    amcPlans = {},
    price = 0,
    amcServiceDetails = {},
    disablePageScroll,
    isLoading,
    list,
  } = action.payload;
  switch (action.type) {
    case actionTypes.RESOLVE: {
      return {
        ...state,
        status: 'resolved',
        selectedProduct: selectedProduct,
      };
    }
    case actionTypes.SET_SELECTED_TIER: {
      return {
        ...state,
        selectedTier: selectedTier,
      };
    }
    case actionTypes.SET_PRODUCT_LIST: {
      return {
        ...state,
        list,
      };
    }
    case actionTypes.SET_SELECTED_MULTIYEAR_PLAN: {
      return {
        ...state,
        selectedMultiYearPlan: selectedMultiYearPlan,
      };
    }
    case actionTypes.SET_AMC_PLANS: {
      return {
        ...state,
        amcPlans: amcPlans,
      };
    }
    case actionTypes.SET_BENEFITS: {
      return {
        ...state,
        benefits: benefits,
      };
    }
    case actionTypes.SET_PER_YEAR_PRICE: {
      return {
        ...state,
        perYearPrice: price,
      };
    }
    case actionTypes.REJECT: {
      return {
        ...state,
        status: 'rejected',
        errorMessage: error.message,
      };
    }
    case actionTypes.SET_AMC_SERVICE_DETAILS: {
      return {
        ...state,
        amcServiceDetails: amcServiceDetails,
      };
    }
    case actionTypes.SET_DISABLE_PAGE_SCROLL: {
      return {
        ...state,
        disablePageScroll: disablePageScroll,
      };
    }
    case actionTypes.SET_ISLOADING: {
      return {
        ...state,
        isLoading: isLoading,
      };
    }
    default:
      throw new Error(`Unsupported type: ${action.type}`);
  }
}

const setProductsData = (dispatch: any) => (selProduct: any) => {
  dispatch({ type: actionTypes.RESOLVE, payload: { selectedProduct: selProduct } });
};

const setProductsList = (dispatch: any) => (list: any[]) => {
  dispatch({ type: actionTypes.SET_PRODUCT_LIST, payload: { list } });
};

const setSelectedTier = (dispatch: any) => (selectedTier: any) => {
  dispatch({ type: actionTypes.SET_SELECTED_TIER, payload: { selectedTier: selectedTier } });
};

const setSelectedMultiYearPlan = (dispatch: any) => (selectedMultiYearPlan: any) => {
  dispatch({
    type: actionTypes.SET_SELECTED_MULTIYEAR_PLAN,
    payload: { selectedMultiYearPlan: selectedMultiYearPlan },
  });
};

const setErrorMsg = (dispatch: any) => (error: Error) => {
  dispatch({ type: actionTypes.REJECT, payload: { error } });
};

const setAMCPlans = (dispatch: any) => (amcPlans: PlancardsProps) => {
  dispatch({ type: actionTypes.SET_AMC_PLANS, payload: { amcPlans: amcPlans } });
};

const setBenefits = (dispatch: any) => (benefits: BenefitsType[]) => {
  dispatch({ type: actionTypes.SET_BENEFITS, payload: { benefits: benefits } });
};

const setPerYearPrice = (dispatch: any) => (price: number) => {
  dispatch({ type: actionTypes.SET_PER_YEAR_PRICE, payload: { price } });
};

const setAmcServiceDetails = (dispatch: any) => (amcServiceDetails: any) => {
  dispatch({ type: actionTypes.SET_AMC_SERVICE_DETAILS, payload: { amcServiceDetails } });
};

const setDisablePageScroll = (dispatch: any) => (disablePageScroll: any) => {
  dispatch({ type: actionTypes.SET_DISABLE_PAGE_SCROLL, payload: { disablePageScroll } });
};

const setIsLoading = (dispatch: any) => (isLoading: any) => {
  dispatch({ type: actionTypes.SET_ISLOADING, payload: { isLoading } });
};

const { Context: ProductContext, Provider: ProductProvider } = createContext<
  productDataType,
  productDispatchType
>(
  productReducer,
  {
    setProductsData,
    setSelectedTier,
    setProductsList,
    setSelectedMultiYearPlan,
    setErrorMsg,
    setAMCPlans,
    setBenefits,
    setPerYearPrice,
    setAmcServiceDetails,
    setDisablePageScroll,
    setIsLoading,
  },
  {
    status: 'pending',
    selectedProduct: null,
    list: [],
    selectedTier: null,
    benefits: [],
    selectedMultiYearPlan: null,
    errorMessage: null,
    perYearPrice: 0,
    amcPlans: { webData: [], mobileData: { pricePlans: [], benefits: [] } },
    amcServiceDetails: null,
    disablePageScroll: false,
    isLoading: false,
  },
);
ProductContext.displayName = 'Product';

export const useProductContext = () => useContext(ProductContext);

export { ProductContext, ProductProvider };
